import React, { useCallback, useEffect, useRef, useState } from 'react';
import Input from './Input';
import Badge from './Badge';
import InlineNotice from './InlineNotice';
import SectionHeading from './SectionHeading';
import DropdownSelect from './DropdownSelect';
import EntitySelector, { type EntitySelectorCreateProps, type EntitySelectorOption } from './EntitySelector';
import CustomDatePicker from './CustomDatePicker';
import ModalShell from './ModalShell';
import PageShell from './PageShell';
import Button from './Button';
import OverflowMenu, { type OverflowMenuItem } from './OverflowMenu';
import SaveButton from './SaveButton';
import FeatureGate from './FeatureGate';
import FileAttachmentZone, {
    type FileAttachmentZoneHandle,
    type FileAttachmentZoneState,
} from './FileAttachmentZone';
import type { UploadErrorTextFn } from '../lib/upload/errorText';
import { useDirtyTracker } from '../hooks/useDirtyTracker';
import { useRegisterUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useToast } from '../contexts/ToastContext';
import { useIsMobile } from '../contexts/ViewportContext';
import { bgLayer1 } from '../lib/layerClasses';
import { ymdInTz } from '../lib/datetime';
import { Icon } from './Icon';
import {
    amountField,
    defaultExpenseAnchor,
    deriveExpense,
    expenseFiguresFromAnalysis,
    parseAmount,
    parseTaxRate,
    recalcExpense,
    type ExpenseAnchor,
    type ExpenseFigure,
    type ExpenseFigures,
} from '../lib/taxMath';

/* ──────────────────────────────────────────────────────────────────────
 * Shared expense detail view — used by BOTH the tenant and platform-admin.
 *
 * Presentational + callback-driven: it owns the form state, dirty tracking,
 * file-attachment staging, AI pre-fill and validation, but delegates all
 * i18n (via `labels`), data and side effects (via callbacks) to the host
 * app. This is what keeps the two apps pixel- and behaviour-identical.
 *
 * Money: `amount` is the TOTAL paid (tax included). With `showTaxFields`
 * the form also holds the taxable base, the tax rate, the tax amount and the
 * deductible share, and keeps them consistent with `recalcExpense`
 * (`lib/taxMath`) — one keystroke derives the others, and whatever still
 * does not add up is flagged, never silently "fixed".
 *
 * AI: an analysed invoice fills the form and every filled field is marked
 * (amber border + "IA" pill) until the user touches it, under a notice asking
 * to review. A supplier the AI read but the workshop does not have is NOT
 * created at analysis time: the picker offers "Nuevo: {name}" and the
 * payload carries `supplier_name`, so the host's backend creates it on save.
 * On desktop the analysed document sits beside the form.
 * ────────────────────────────────────────────────────────────────────── */

export interface ExpenseFileLike {
    id: string | number;
    file_type?: string;
    file_name?: string;
    file_url?: string;
    file_size?: string;
}

export interface ExpenseLike {
    id?: number;
    expense_number?: number;
    description?: string;
    date?: string;
    /** TOTAL paid, tax included. */
    amount?: number;
    /** Taxable base (the net). `null` = not split. */
    base_amount?: number | null;
    /** Tax rate in % (`21` = 21 %). `null` = unknown or several rates. */
    tax_rate?: number | null;
    tax_amount?: number | null;
    /** Share of the expense that is deductible, 0..100 (default 100). */
    deductible_pct?: number | null;
    delivery_note?: string | null;
    invoice_number?: string | null;
    invoice_date?: string | null;
    supplier_id?: number | null;
    /** Two roles, one per direction.
     *
     *  READ (host → view): the name of the currently-linked supplier, so the
     *  picker can show the selection WITHOUT holding a list. Hosts fill it
     *  from the API's hydrated `supplier` object. Without it, editing an
     *  expense whose supplier fell outside any pre-loaded window would render
     *  an empty field.
     *
     *  WRITE (view → `onSave`): set ONLY when the user kept the "Nuevo:
     *  {name}" supplier an analysis proposed — `supplier_id` is then null and
     *  the backend creates (or matches) the supplier by this name on save.
     *  Never echoed otherwise, so clearing the picker can't re-link the old
     *  supplier by name. */
    supplier_name?: string | null;
    /** ISO currency of the expense (only with the `currency` prop). */
    currency?: string | null;
    /** Where the money goes — a key of the host's list (only with the
     *  `categories` prop); `null` = none. */
    category?: string | null;
    /** WRITE only: a rate the user typed (1 expense unit in the workshop
     *  currency). Never read back into the form: the stored rate is shown
     *  by the host's `renderFooter`, and sending it back would turn a
     *  reference rate into a typed one. */
    fx_rate?: number | null;
    files?: ExpenseFileLike[];
}

/** The expense's currency and a typed rate (documentation/Multi_Currency.md).
 *  The Admin omits it: its expenses are in euros. */
export interface ExpenseCurrencyControls {
    options: ReadonlyArray<{ value: string; label: string }>;
    /** The workshop currency: the default, and the one a rate converts to. */
    workshopCurrency: string;
    label: string;
    rateLabel: (currency: string) => string;
    rateHint: string;
    searchPlaceholder?: string;
    /** Under the two fields: the estimate, the rate's source, the refresh button. */
    renderFooter?: (currency: string, typedRate: number | null) => React.ReactNode;
}

/** The expense's category (the tenant's fixed list). The Admin omits it. */
export interface ExpenseCategoryControls {
    label: string;
    /** The «none» choice, first in the list. */
    noneLabel: string;
    options: ReadonlyArray<{ value: string; label: string }>;
}

export interface ExpenseSupplierOption {
    id: number;
    name: string;
}

/** «Crear proveedor» at the end of the supplier list. The host opens its own
 *  supplier form over the expense (so nothing typed or attached here is lost)
 *  and resolves with the supplier it created — or `null` if the form was
 *  closed without saving. The view then picks it. */
export interface ExpenseCreateSupplierControls {
    label: string;
    /** Receives what was typed in the picker, to start the form with it. */
    open: (typedName: string) => Promise<ExpenseSupplierOption | null>;
}

export interface ExpenseAnalysisLike {
    description?: string | null;
    /** Total paid (legacy name; `total` wins when both are present). */
    amount?: number | null;
    base_amount?: number | null;
    tax_rate?: number | null;
    tax_amount?: number | null;
    total?: number | null;
    date?: string | null;
    invoice_date?: string | null;
    invoice_number?: string | null;
    delivery_note?: string | null;
    supplier_name?: string | null;
    /** Set when the name matched a supplier the host already has. */
    supplier_id?: number | null;
    /** How sure the model is: `'low'` turns the review notice into a warning. */
    confidence?: string | null;
    /** A category key the model proposed (kept only if it is an option). */
    category?: string | null;
    /** The invoice's own figures do not add up (base + tax ≠ total). */
    totals_mismatch?: boolean | null;
}

export interface ExpenseDetailLabels {
    newExpense: string;
    editExpense: (id: string) => string;
    subtitle: string;
    subtitleEdit: string;
    description: string;
    descriptionPlaceholder: string;
    date: string;
    /** The single amount field (`showTaxFields={false}`). */
    amount: string;
    amountPlaceholder: string;
    documentation: string;
    receiptNumber: string;
    supplier: string;
    selectSupplier: string;
    /** The picker found nothing. Default: `selectSupplier`. */
    noSuppliers?: string;
    invoiceNumber: string;
    invoiceDate: string;
    attachments: string;
    addFiles: string;
    uploadAI: string;
    processing: string;
    save: string;
    saving: string;
    saved: string;
    deleteExpense: string;
    errDescRequired: string;
    errDateRequired: string;
    errAmountRequired: string;
    errSave: string;
    errAnalyze: string;
    download: string;
    delete: string;
    undo: string;
    /** Optional date-picker localization — defaults to Spanish (admin). */
    monthNames?: string[];
    dayNames?: string[];
    datePlaceholder?: string;
    /* ── Tax split + AI review. Optional: Spanish defaults (the admin is
     *    Spanish-only); the tenant passes them through `t()`. ── */
    baseAmount?: string;
    taxRate?: string;
    taxAmount?: string;
    /** The total field (tax included) when `showTaxFields`. */
    total?: string;
    deductiblePct?: string;
    /** Receives the difference already formatted with `formatMoney`. */
    mismatchWarning?: (diff: string) => string;
    aiReview?: string;
    aiLowConfidence?: string;
    aiBadge?: string;
    newSupplier?: (name: string) => string;
    newSupplierHint?: string;
    analysedDocument?: string;
    errTaxRateRange?: string;
    errDeductibleRange?: string;
    /** A negative base or tax (0 is valid for both — not the total's rule). */
    errAmountsNegative?: string;
}

type TaxLabelKey =
    | 'baseAmount' | 'taxRate' | 'taxAmount' | 'total' | 'deductiblePct'
    | 'mismatchWarning' | 'aiReview' | 'aiLowConfidence' | 'aiBadge'
    | 'newSupplier' | 'newSupplierHint' | 'analysedDocument'
    | 'errTaxRateRange' | 'errDeductibleRange' | 'errAmountsNegative';

const DEFAULT_TAX_LABELS: Required<Pick<ExpenseDetailLabels, TaxLabelKey>> = {
    baseAmount: 'Base imponible',
    taxRate: 'Impuesto %',
    taxAmount: 'Importe del impuesto',
    total: 'Total',
    deductiblePct: 'Deducible %',
    mismatchWarning: (diff) => `Base + impuesto no cuadra con el total (diferencia ${diff})`,
    aiReview: 'Revisa los datos extraídos por la IA antes de guardar.',
    aiLowConfidence: 'La IA no está segura de algunos datos: compruébalos con la factura.',
    aiBadge: 'IA',
    newSupplier: (name) => `Nuevo: ${name}`,
    newSupplierHint: 'El proveedor se creará al guardar el gasto',
    analysedDocument: 'Documento analizado',
    errTaxRateRange: 'El impuesto debe estar entre 0 y 100 %',
    errDeductibleRange: 'El % deducible debe estar entre 0 y 100',
    errAmountsNegative: 'La base y el impuesto no pueden ser negativos',
};

export interface ExpenseDetailViewProps {
    expense: ExpenseLike | null;
    /** Search suppliers on the SERVER. Called with the typed query (and with an
     *  empty string when the picker opens, to show the first ones).
     *
     *  This replaced a pre-loaded `suppliers` array: the picker filtered only
     *  inside whatever the host had fetched, so any supplier past that window
     *  was invisible — and silently, since the field just showed no match. The
     *  list is no longer capped because the component never holds one. */
    searchSuppliers: (query: string) => Promise<ExpenseSupplierOption[]>;
    labels: ExpenseDetailLabels;
    /** `'modal'` (default) renders inside `ModalShell`; `'page'` via `PageShell`. */
    variant?: 'modal' | 'page';
    onClose: () => void;
    /** Persist the payload. May resolve to the SAVED expense (with the
     *  supplier the backend created, the figures it normalised and the files
     *  it stored); the view then adopts it as its new clean state. Resolving
     *  to nothing keeps the form as typed. */
    onSave: (payload: ExpenseLike) => void | Promise<void | ExpenseLike | null | undefined>;
    /** Called after a successful save (page variant): navigate / refresh. */
    onSaved?: () => void | Promise<void>;
    /** When provided AND editing, shows a 3-dot overflow menu with delete. */
    onDelete?: () => void;
    /** When provided, renders the "Subir con IA" button. */
    onAnalyze?: (file: File) => Promise<ExpenseAnalysisLike>;
    /** When true, the AI button still renders but is greyed + locked with a
     *  tooltip (plan entitlement gate). Host keeps passing `onAnalyze` so the
     *  control stays visible — it just can't be used. Admin omits this (always
     *  unlocked). */
    analyzeLocked?: boolean;
    /** Tooltip shown on the locked AI button, e.g. "Disponible a partir del plan Pro". */
    analyzeLockedMessage?: string;
    /** Optional partial pre-fill (used by an AI-drop flow on the list page). */
    initialDraft?: Partial<ExpenseLike>;
    /** The date a new expense starts on (`YYYY-MM-DD`). Tenant passes the
     *  WORKSHOP's day; default the viewer's own day, never the UTC one. */
    today?: string;
    /** Temp-upload endpoint base + auth — forwarded to FileAttachmentZone. */
    uploadBaseUrl?: string;
    uploadAuthMode?: 'bearer' | 'cookie';
    /** Localize a save error before it's surfaced. Tenant passes a
     *  `translateApiError(err, t)`-based fn so backend 4xx/422 render in the
     *  user's language; Admin omits it (Spanish-only) and the raw message shows. */
    translateError?: (err: unknown) => string;
    /** What a failed attachment upload says — forwarded to
     *  `FileAttachmentZone`'s `uploadErrorText`. Tenant passes its
     *  translating `uploadErrorText(t)`; Admin omits it (Spanish default). */
    uploadErrorText?: UploadErrorTextFn;
    /** Base imponible · Impuesto % · Importe del impuesto · Total + Deducible %
     *  (default `true`). `false` keeps the single amount field, and the
     *  payload carries none of the tax fields — for a host whose backend does
     *  not store them. */
    showTaxFields?: boolean;
    /** Formats a money figure for display (the mismatch warning). Defaults to
     *  two decimals without a currency. */
    formatMoney?: (value: number) => string;
    currency?: ExpenseCurrencyControls;
    /** Shows the category picker and sends `category` (the tenant). */
    categories?: ExpenseCategoryControls;
    /** Adds «Crear proveedor» to the supplier picker. */
    createSupplier?: ExpenseCreateSupplierControls;
}

/** Fields an analysis can fill — each one is marked until the user edits it. */
type AiKey =
    | 'description' | 'date' | 'invoice_date' | 'invoice_number' | 'delivery_note' | 'supplier'
    | 'amount' | 'base_amount' | 'tax_rate' | 'tax_amount' | 'category';

/** Internal form state: every control's raw value (money as text, so a
 *  half-typed "12." survives a render); `buildPayload` coerces on save.
 *  Everything here is JSON — the dirty tracker serialises it. */
interface ExpenseFormState {
    description: string;
    date: string;
    /** TOTAL paid. */
    amount: string;
    base_amount: string;
    tax_rate: string;
    tax_amount: string;
    deductible_pct: string;
    delivery_note: string;
    invoice_number: string;
    invoice_date: string;
    supplier_id: number | null;
    /** The "Nuevo: {name}" supplier is picked: the host creates it on save. */
    pending_supplier_name: string | null;
    currency: string;
    /** The typed rate; '' = the reference one. */
    fx_rate_manual: string;
    /** A category key; '' = none. */
    category: string;
    files: ExpenseFileLike[];
}

/** Form field ↔ `taxMath` figure. */
const FIGURE_FIELD: Record<ExpenseFigure, 'base_amount' | 'tax_rate' | 'tax_amount' | 'amount'> = {
    base: 'base_amount',
    rate: 'tax_rate',
    tax: 'tax_amount',
    total: 'amount',
};

const figuresOf = (f: ExpenseFormState): ExpenseFigures => ({
    base: f.base_amount,
    rate: f.tax_rate,
    tax: f.tax_amount,
    total: f.amount,
});

const withFigures = (f: ExpenseFormState, fig: ExpenseFigures): ExpenseFormState => ({
    ...f,
    base_amount: fig.base,
    tax_rate: fig.rate,
    tax_amount: fig.tax,
    amount: fig.total,
});

const dayOf = (v: string | null | undefined): string => v?.split('T')[0] ?? '';
const rateField = (n: number | null | undefined) =>
    typeof n === 'number' && Number.isFinite(n) ? String(n) : '';

const toFormState = (src: Partial<ExpenseLike>): ExpenseFormState => ({
    description: src.description ?? '',
    date: dayOf(src.date),
    amount: amountField(src.amount),
    base_amount: amountField(src.base_amount),
    tax_rate: rateField(src.tax_rate),
    tax_amount: amountField(src.tax_amount),
    deductible_pct: rateField(src.deductible_pct ?? 100),
    delivery_note: src.delivery_note ?? '',
    invoice_number: src.invoice_number ?? '',
    invoice_date: dayOf(src.invoice_date),
    supplier_id: src.supplier_id ?? null,
    pending_supplier_name: null,
    currency: (src.currency || '').toUpperCase(),
    fx_rate_manual: '',
    category: src.category ?? '',
    files: src.files ?? [],
});

/** What the supplier picker holds: a real supplier, or the one to create. */
type SupplierPick = { kind: 'existing'; id: number } | { kind: 'new'; name: string };

/** Only formats a browser can render inline get a side pane (no HEIC/TIFF). */
const previewKindOf = (file: File): 'pdf' | 'image' | null => {
    if (file.type === 'application/pdf') return 'pdf';
    if (/^image\/(png|jpe?g|webp|gif|bmp|svg\+xml|avif)$/i.test(file.type)) return 'image';
    return null;
};

/* Amber border on a marked control. Input takes it through `className`;
 * the date picker and the supplier picker have no such prop, so the wrapper
 * reaches their trigger (`.input-container-box` / the first button). */
const AI_INPUT_CLASS = '!border-amber-500 dark:!border-amber-400';
const AI_DATE_WRAP_CLASS =
    '[&_.input-container-box]:!border-amber-500 dark:[&_.input-container-box]:!border-amber-400';
// The trigger's value span gets room for the pill, so a long name truncates
// before it instead of running underneath.
const AI_SELECT_WRAP_CLASS =
    '[&>div>button]:!border-amber-500 dark:[&>div>button]:!border-amber-400 [&>div>button>span:first-child]:!pr-12';

/* Column counts follow the SECTION's width (`@container` on each section), not
 * the viewport's: both hosts put a 256 px sidebar beside the page from `md`,
 * and the document pane takes two fifths of what is left, so a viewport
 * breakpoint said "wide" to a form column a third of that.
 *
 * Money: one per row below 20rem (a phone: two columns there clip the labels,
 * and a total with its "IA" pill, in es/it/fr); then base | rate over
 * tax | total, the text-heavy pair on the wider track ("Importe del impuesto"
 * needs ~141 px of label); from 48rem the four in a row. */
const MONEY_GRID_CLASS =
    'grid grid-cols-1 @[20rem]:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] @3xl:grid-cols-4 gap-4';
const PAIR_GRID_CLASS = 'grid grid-cols-1 @md:grid-cols-2 gap-4';
const SECTION_CLASS = `@container ${bgLayer1} p-6 rounded-xl border border-border-subtle dark:border-border-subtle-dark shadow-sm`;

const ExpenseDetailView: React.FC<ExpenseDetailViewProps> = ({
    expense,
    searchSuppliers,
    labels: labelsIn,
    variant = 'modal',
    onClose,
    onSave,
    onSaved,
    onDelete,
    onAnalyze,
    analyzeLocked = false,
    analyzeLockedMessage,
    initialDraft,
    today,
    uploadBaseUrl,
    uploadAuthMode,
    uploadErrorText,
    translateError,
    showTaxFields = true,
    formatMoney = (value: number) => value.toFixed(2),
    currency: currencyControls,
    categories,
    createSupplier,
}) => {
    const labels = { ...DEFAULT_TAX_LABELS, ...labelsIn };
    const { toast } = useToast();
    const isMobile = useIsMobile();
    // Localize a thrown error (backend 4xx/422) for the toast + inline banner;
    // falls back to the raw message when no translator is supplied (Admin).
    const errMsg = (error: unknown): string =>
        translateError?.(error) || (error as Error)?.message || labels.errSave;
    const isNew = expense === null;
    const [editable, setEditable] = useState<ExpenseFormState>(() =>
        expense
            ? toFormState(expense)
            : toFormState({ date: today ?? ymdInTz(new Date()), ...(initialDraft || {}) }),
    );
    // A supplier the admin's analyser just created may be missing from a
    // supplier list the host fetched before — keep those locally so the
    // picker can still find them.
    const [aiSuppliers, setAiSuppliers] = useState<ExpenseSupplierOption[]>([]);
    // The name an analysis read and no supplier matched: offered as
    // "Nuevo: {name}" for as long as the view lives, picked or not.
    const [aiNewSupplier, setAiNewSupplier] = useState<string | null>(null);
    const [aiFilled, setAiFilled] = useState<ReadonlySet<AiKey>>(() => new Set());
    // null = no analysis yet (no notice); otherwise the model's confidence.
    const [aiReview, setAiReview] = useState<{ lowConfidence: boolean } | null>(null);
    const [analysedPreview, setAnalysedPreview] = useState<{ url: string; kind: 'pdf' | 'image'; name: string } | null>(null);
    const previewUrlRef = useRef<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const aiInputRef = useRef<HTMLInputElement>(null);
    // What the money keystrokes derive FROM (`ExpenseAnchor`). A ref: it never
    // renders anything, and keeping it out of `editable` keeps it out of the
    // dirty tracker.
    const anchorRef = useRef<ExpenseAnchor>(defaultExpenseAnchor(figuresOf(editable)));

    // The object URL of the analysed file lives as long as the view.
    useEffect(() => () => {
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    }, []);

    const fileZoneRef = useRef<FileAttachmentZoneHandle>(null);
    const [fileZoneState, setFileZoneState] = useState<FileAttachmentZoneState>({
        pendingAdds: [], pendingRemovals: [], isDirty: false,
    });

    // Opt out of the auto-guard: `useDirtyTracker` only sees `editable` (the
    // text/amount/supplier fields) — not the staged attachments. We register
    // the COMPOSITE `hasUnsavedChanges` below so adding/removing a file alone
    // still warns on navigate-away. Excludes `isNew` so a pristine new expense
    // doesn't false-warn.
    const tracker = useDirtyTracker(editable, { guardNavigation: false });
    const { markClean: trackerMarkClean, reset: trackerReset } = tracker;
    const hasUnsavedChanges = tracker.isDirty || fileZoneState.isDirty;
    // Even when creating, the save button stays disabled until there's a real
    // change (fields or attachments) — CLAUDE.md form-dirty rule. Required fields
    // are still validated in the save handler.
    const isDirty = hasUnsavedChanges;
    useRegisterUnsavedChanges(hasUnsavedChanges);

    // The picked supplier, kept as {id,name} so the closed field can show a name
    // the component never had a list for. Seeded from the expense's own hydrated
    // supplier; updated on every pick.
    const [selectedSupplier, setSelectedSupplier] = useState<ExpenseSupplierOption | null>(
        expense?.supplier_id
            ? { id: expense.supplier_id, name: expense.supplier_name || String(expense.supplier_id) }
            : null,
    );

    const newSupplierOption = useCallback(
        (name: string): EntitySelectorOption<SupplierPick> => ({
            id: 'new-supplier',
            label: labels.newSupplier(name),
            subtitle: labels.newSupplierHint,
            value: { kind: 'new', name },
        }),
        // Both are stable: the host's memoised/module labels or the defaults.
        [labels.newSupplier, labels.newSupplierHint],
    );

    /** Server search, with what the analysis proposed merged on top.
     *
     *  "Nuevo: {name}" goes FIRST while it matches the query, and so do the
     *  suppliers the admin's analyser created (the host's list may predate
     *  them). */
    const runSupplierSearch = useCallback(async (query: string): Promise<EntitySelectorOption<SupplierPick>[]> => {
        const q = query.trim().toLowerCase();
        const local = aiSuppliers.filter((s) => !q || s.name.toLowerCase().includes(q));
        let remote: ExpenseSupplierOption[] = [];
        try {
            remote = await searchSuppliers(query);
        } catch {
            // Keep the AI-proposed ones usable even if the lookup fails.
        }
        const seen = new Set(local.map((s) => s.id));
        const merged = [...local, ...remote.filter((s) => !seen.has(s.id))];
        const options: EntitySelectorOption<SupplierPick>[] = merged.map((s) => ({
            id: String(s.id),
            label: s.name,
            value: { kind: 'existing', id: s.id },
        }));
        if (aiNewSupplier && (!q || aiNewSupplier.toLowerCase().includes(q))) {
            options.unshift(newSupplierOption(aiNewSupplier));
        }
        return options;
    }, [aiSuppliers, aiNewSupplier, newSupplierOption, searchSuppliers]);

    const unmarkAi = (keys: AiKey[]) =>
        setAiFilled((prev) => {
            if (!keys.some((k) => prev.has(k))) return prev;
            const next = new Set(prev);
            keys.forEach((k) => next.delete(k));
            return next;
        });

    const setField = <K extends keyof ExpenseFormState>(name: K, value: ExpenseFormState[K]) => {
        setEditable((prev) => ({ ...prev, [name]: value }));
        unmarkAi([name as AiKey]);
    };

    /** A money keystroke: the edited figure plus whatever it derives. The
     *  derived ones stop being "what the AI read" too. */
    const setFigure = (fig: ExpenseFigure, value: string) => {
        const { next, derived, anchor } = recalcExpense(figuresOf(editable), fig, value, anchorRef.current);
        anchorRef.current = anchor;
        setEditable((prev) => withFigures(prev, { ...figuresOf(prev), ...pickFigures(next, [fig, ...derived]) }));
        unmarkAi([fig, ...derived].map((f) => FIGURE_FIELD[f]));
    };

    const showPreview = (file: File) => {
        const kind = previewKindOf(file);
        if (!kind) return;
        const url = URL.createObjectURL(file);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setAnalysedPreview({ url, kind, name: file.name });
    };

    const handleAiImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (e.target) e.target.value = '';
        if (!file || !onAnalyze || analyzeLocked) return;
        setIsAnalyzing(true);
        setSaveError(null);
        try {
            const result = await onAnalyze(file);
            const filled = new Set<AiKey>();
            const date = dayOf(result.date);
            const invoiceDate = dayOf(result.invoice_date);
            if (result.description) filled.add('description');
            if (date) filled.add('date');
            if (invoiceDate) filled.add('invoice_date');
            if (result.invoice_number) filled.add('invoice_number');
            if (result.delivery_note) filled.add('delivery_note');
            const aiCategory = categories && result.category
                && categories.options.some((o) => o.value === result.category) ? result.category : null;
            if (aiCategory) filled.add('category');

            // Money comes as ONE block from the same invoice.
            const figures = showTaxFields ? expenseFiguresFromAnalysis(result) : null;
            const plainTotal = parseAmount(result.total ?? null) ?? parseAmount(result.amount ?? null);
            if (figures) {
                (Object.keys(FIGURE_FIELD) as ExpenseFigure[])
                    .filter((f) => figures[f] !== '')
                    .forEach((f) => filled.add(FIGURE_FIELD[f]));
                // The invoice's bottom line is what was paid: a rate corrected
                // afterwards splits it. Only a base read without it anchors there.
                anchorRef.current = plainTotal !== null ? 'total' : 'base';
            } else if (!showTaxFields && plainTotal !== null) {
                filled.add('amount');
            }

            // Supplier: a match is selected; an unknown name is proposed as new.
            const matchedId = result.supplier_id ?? null;
            const readName = result.supplier_name?.trim() || null;
            let supplierPatch: Pick<ExpenseFormState, 'supplier_id' | 'pending_supplier_name'> | null = null;
            if (matchedId) {
                const picked = { id: matchedId, name: readName || String(matchedId) };
                setSelectedSupplier(picked);
                setAiSuppliers((prev) => (prev.find((s) => s.id === picked.id) ? prev : [...prev, picked]));
                supplierPatch = { supplier_id: matchedId, pending_supplier_name: null };
                filled.add('supplier');
            } else if (readName) {
                setAiNewSupplier(readName);
                setSelectedSupplier(null);
                supplierPatch = { supplier_id: null, pending_supplier_name: readName };
                filled.add('supplier');
            }

            setEditable((prev) => {
                let updated: ExpenseFormState = {
                    ...prev,
                    description: result.description || prev.description,
                    date: date || prev.date,
                    invoice_date: invoiceDate || prev.invoice_date,
                    invoice_number: result.invoice_number || prev.invoice_number,
                    delivery_note: result.delivery_note || prev.delivery_note,
                    category: aiCategory || prev.category,
                    ...(supplierPatch ?? {}),
                };
                if (figures) updated = withFigures(updated, figures);
                else if (!showTaxFields && plainTotal !== null) updated.amount = amountField(plainTotal);
                return updated;
            });
            setAiFilled(filled);
            setAiReview({ lowConfidence: result.confidence === 'low' || result.totals_mismatch === true });
            showPreview(file);
            // Attach the analysed document to the expense (temp upload → pending).
            await fileZoneRef.current?.uploadAndAdd(file);
        } catch (error) {
            // Prefer the host's translator so a typed ApiError renders as
            // errors.<code> in the user's language. Without it a structured
            // detail object printed as "[object Object]".
            const msg = translateError?.(error) || (error as Error)?.message || labels.errAnalyze;
            toast.error(msg);
            setSaveError(msg);
        } finally {
            setIsAnalyzing(false);
        }
    };

    const validate = (): boolean => {
        const total = parseAmount(editable.amount);
        if (!editable.description.trim()) { toast.error(labels.errDescRequired); return false; }
        if (!editable.date.trim()) { toast.error(labels.errDateRequired); return false; }
        if (total === null || total <= 0) { toast.error(labels.errAmountRequired); return false; }
        if (showTaxFields) {
            const base = parseAmount(editable.base_amount);
            const tax = parseAmount(editable.tax_amount);
            if ((base !== null && base < 0) || (tax !== null && tax < 0)) {
                toast.error(labels.errAmountsNegative);
                return false;
            }
            if (!parseTaxRate(editable.tax_rate).valid) { toast.error(labels.errTaxRateRange); return false; }
            const deductible = parseAmount(editable.deductible_pct);
            if (deductible === null || deductible < 0 || deductible > 100) {
                toast.error(labels.errDeductibleRange);
                return false;
            }
        }
        return true;
    };

    const buildPayload = (): ExpenseLike => {
        const keptExisting = editable.files.filter((f) => !fileZoneState.pendingRemovals.includes(f.id));
        const newFiles: ExpenseFileLike[] = fileZoneState.pendingAdds.map((p) => ({
            id: p.tempId,
            file_type: p.file_type,
            file_name: p.file_name,
            file_url: p.file_url,
            file_size: p.file_size,
        }));
        const pendingName = editable.pending_supplier_name;
        const payload: ExpenseLike = {
            id: expense?.id,
            expense_number: expense?.expense_number,
            description: editable.description,
            date: editable.date,
            amount: parseAmount(editable.amount) ?? 0,
            delivery_note: editable.delivery_note,
            invoice_number: editable.invoice_number,
            // Optional date control holds '' when empty; send null (not '') so the
            // backend's Optional[datetime] doesn't reject an empty string. Mirrors
            // how orders normalize their dates in mapOrderToBackend.
            invoice_date: editable.invoice_date.trim() ? editable.invoice_date : null,
            supplier_id: pendingName ? null : editable.supplier_id,
            files: [...keptExisting, ...newFiles],
        };
        // Only a picked "Nuevo: {name}" travels (see `ExpenseLike.supplier_name`).
        if (pendingName) payload.supplier_name = pendingName;
        if (categories) payload.category = editable.category || null;
        if (currencyControls) {
            const cur = editable.currency || currencyControls.workshopCurrency;
            payload.currency = cur;
            const typed = parseAmount(editable.fx_rate_manual);
            if (typed !== null && typed > 0 && cur !== currencyControls.workshopCurrency) payload.fx_rate = typed;
        }
        if (showTaxFields) {
            // Empty = null on purpose: an update must be able to clear a figure,
            // and the backend derives what is missing from what is present.
            payload.base_amount = parseAmount(editable.base_amount);
            payload.tax_rate = parseTaxRate(editable.tax_rate).value;
            payload.tax_amount = parseAmount(editable.tax_amount);
            payload.deductible_pct = parseAmount(editable.deductible_pct) ?? 100;
        }
        return payload;
    };

    /** After a save: the review is over, and whatever the host returned (the
     *  created supplier, the normalised figures, the stored files) becomes the
     *  new clean baseline. */
    const afterSave = (saved: void | ExpenseLike | null | undefined) => {
        setAiFilled(new Set());
        setAiReview(null);
        if (!saved || typeof saved !== 'object') {
            trackerMarkClean();
            return;
        }
        const next: ExpenseFormState = { ...editable };
        if (saved.supplier_id != null) {
            next.supplier_id = saved.supplier_id;
            next.pending_supplier_name = null;
            setSelectedSupplier({
                id: saved.supplier_id,
                name: saved.supplier_name || editable.pending_supplier_name || selectedSupplier?.name || String(saved.supplier_id),
            });
            if (editable.pending_supplier_name) setAiNewSupplier(null);
        }
        if (typeof saved.amount === 'number') next.amount = amountField(saved.amount);
        if (categories && saved.category !== undefined) next.category = saved.category ?? '';
        if (showTaxFields) {
            if (saved.base_amount !== undefined) next.base_amount = amountField(saved.base_amount);
            if (saved.tax_rate !== undefined) next.tax_rate = rateField(saved.tax_rate);
            if (saved.tax_amount !== undefined) next.tax_amount = amountField(saved.tax_amount);
            if (typeof saved.deductible_pct === 'number') next.deductible_pct = rateField(saved.deductible_pct);
            // An anchor the server left empty has nothing to derive from.
            if (anchorRef.current === 'base' && next.base_amount === '') anchorRef.current = 'total';
        }
        if (Array.isArray(saved.files)) next.files = saved.files;
        if (typeof saved.currency === 'string') next.currency = saved.currency.toUpperCase();
        next.fx_rate_manual = '';
        setEditable(next);
        trackerReset(next);
    };

    // Modal variant: close after a successful save.
    const handleSaveModal = async () => {
        setSaveError(null);
        if (!validate()) return;
        setIsSaving(true);
        try {
            await onSave(buildPayload());
            fileZoneRef.current?.markCommitted();
            trackerMarkClean();
            onClose();
        } catch (error) {
            const msg = errMsg(error);
            toast.error(msg);
            setSaveError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    // Page variant (SaveButton): false aborts (validation), throw stays.
    const handleSavePage = async (): Promise<boolean | void> => {
        setSaveError(null);
        if (!validate()) return false;
        try {
            const saved = await onSave(buildPayload());
            fileZoneRef.current?.markCommitted();
            afterSave(saved);
        } catch (error) {
            const msg = errMsg(error);
            toast.error(msg);
            setSaveError(msg);
            throw error;
        }
    };

    const handleClose = async () => {
        await fileZoneRef.current?.discardPending();
        onClose();
    };

    const Shell = variant === 'page' ? PageShell : ModalShell;
    const showPane = !isMobile && analysedPreview !== null;

    const aiOn = (key: AiKey) => aiFilled.has(key);
    // Opaque behind the pill: it sits over a field border or a value.
    const aiPill = (
        <span className={`inline-flex rounded-full ${bgLayer1}`}>
            <Badge tone="warning" className="!px-1.5 !py-0">{labels.aiBadge}</Badge>
        </span>
    );
    const aiInputProps = (key: AiKey) =>
        aiOn(key) ? { className: AI_INPUT_CLASS, suffix: aiPill } : {};
    /** The pill for the two controls without a `suffix`: over the trigger,
     *  left of its own icon. Both triggers are `h-10` at the wrapper's bottom. */
    const aiOverlay = (key: AiKey) =>
        aiOn(key) ? (
            <span className="pointer-events-none absolute bottom-[11px] right-9 z-10">{aiPill}</span>
        ) : null;

    // What the server will store — it completes the missing figures before it
    // checks them, so this is what the warning (and the greyed hints in empty
    // fields) must describe, not only what was typed.
    const preview = showTaxFields ? deriveExpense(figuresOf(editable)) : null;
    const mismatch = preview?.mismatch ?? null;
    const rateInvalid = showTaxFields && !parseTaxRate(editable.tax_rate).valid;
    const isNegative = (raw: string) => (parseAmount(raw) ?? 0) < 0;
    const negativeError = (raw: string) => (isNegative(raw) ? labels.errAmountsNegative : undefined);
    /** An empty base/tax shows, greyed, the figure the server will store. */
    const hint = (raw: string, derivedValue: number | null | undefined) =>
        raw.trim() === '' && typeof derivedValue === 'number' && derivedValue >= 0
            ? amountField(derivedValue)
            : labels.amountPlaceholder;

    const supplierValue: EntitySelectorOption<SupplierPick> | null = editable.pending_supplier_name
        ? newSupplierOption(editable.pending_supplier_name)
        : selectedSupplier
            ? {
                  id: String(selectedSupplier.id),
                  label: selectedSupplier.name,
                  value: { kind: 'existing', id: selectedSupplier.id },
              }
            : null;

    const onSupplierChange = (opt: EntitySelectorOption<SupplierPick> | null) => {
        const pick = opt?.value ?? null;
        if (pick?.kind === 'new') {
            setSelectedSupplier(null);
            setEditable((prev) => ({ ...prev, supplier_id: null, pending_supplier_name: pick.name }));
        } else {
            setSelectedSupplier(pick && opt ? { id: pick.id, name: opt.label } : null);
            setEditable((prev) => ({ ...prev, supplier_id: pick ? pick.id : null, pending_supplier_name: null }));
        }
        unmarkAi(['supplier']);
    };

    const supplierCreateProps: EntitySelectorCreateProps = createSupplier
        ? {
            createNewLabel: createSupplier.label,
            onCreateNew: async (typed: string) => {
                const created = await createSupplier.open(typed);
                if (!created) return;
                onSupplierChange({
                    id: String(created.id),
                    label: created.name,
                    value: { kind: 'existing', id: created.id },
                });
            },
        }
        : {};

    const form = (
        <div className="space-y-6 min-w-0">
            {saveError && (
                <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                    <Icon name="error" className="text-red-500 mt-0.5" />
                    <p className="text-red-600 dark:text-red-300 text-xs leading-snug">{saveError}</p>
                </div>
            )}

            {aiReview && (
                aiReview.lowConfidence ? (
                    <InlineNotice tone="warning" icon="psychology" title={labels.aiReview}>
                        {labels.aiLowConfidence}
                    </InlineNotice>
                ) : (
                    <InlineNotice tone="info" icon="psychology" title={labels.aiReview} />
                )
            )}

            <section id="tour-expense-basic" className={`${SECTION_CLASS} space-y-4`}>
                <SectionHeading icon="info" className="mb-3">{labels.description}</SectionHeading>
                <Input
                    label={labels.description}
                    markRequired
                    value={editable.description}
                    onChange={(e) => setField('description', e.target.value)}
                    placeholder={labels.descriptionPlaceholder}
                    icon="description"
                    {...aiInputProps('description')}
                />
                {categories && (
                    <div className={`relative min-w-0 ${aiOn('category') ? AI_SELECT_WRAP_CLASS : ''}`}>
                        <DropdownSelect
                            label={categories.label}
                            icon="category"
                            value={editable.category}
                            options={[{ value: '', label: categories.noneLabel }, ...categories.options]}
                            onChange={(v) => setField('category', v)}
                        />
                        {aiOverlay('category')}
                    </div>
                )}
                {showTaxFields ? (
                    <>
                        <div className={PAIR_GRID_CLASS}>
                            <div className={`relative min-w-0 ${aiOn('date') ? AI_DATE_WRAP_CLASS : ''}`}>
                                <CustomDatePicker
                                    label={labels.date}
                    markRequired
                                    selectedDate={editable.date}
                                    onChange={(date) => setField('date', date)}
                                    monthNames={labels.monthNames}
                                    dayNames={labels.dayNames}
                                    placeholder={labels.datePlaceholder}
                                />
                                {aiOverlay('date')}
                            </div>
                        </div>
                        <div className={MONEY_GRID_CLASS}>
                            <Input
                                label={labels.baseAmount}
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={editable.base_amount}
                                onChange={(e) => setFigure('base', e.target.value)}
                                placeholder={hint(editable.base_amount, preview?.base)}
                                error={negativeError(editable.base_amount)}
                                {...aiInputProps('base_amount')}
                            />
                            <Input
                                label={labels.taxRate}
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                inputMode="decimal"
                                value={editable.tax_rate}
                                onChange={(e) => setFigure('rate', e.target.value)}
                                placeholder="—"
                                error={rateInvalid ? labels.errTaxRateRange : undefined}
                                {...aiInputProps('tax_rate')}
                            />
                            <Input
                                label={labels.taxAmount}
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={editable.tax_amount}
                                onChange={(e) => setFigure('tax', e.target.value)}
                                placeholder={hint(editable.tax_amount, preview?.tax)}
                                error={negativeError(editable.tax_amount)}
                                {...aiInputProps('tax_amount')}
                            />
                            <Input
                                label={labels.total}
                    markRequired
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={editable.amount}
                                onChange={(e) => setFigure('total', e.target.value)}
                                placeholder={labels.amountPlaceholder}
                                {...aiInputProps('amount')}
                            />
                        </div>
                        {mismatch !== null && (
                            <InlineNotice tone="danger" icon="calculate">
                                {labels.mismatchWarning(formatMoney(Math.abs(mismatch)))}
                            </InlineNotice>
                        )}
                        <div className={MONEY_GRID_CLASS}>
                            {currencyControls && (() => {
                                const cur = editable.currency || currencyControls.workshopCurrency;
                                const foreign = cur !== currencyControls.workshopCurrency;
                                return (
                                    <>
                                        <DropdownSelect
                                            label={currencyControls.label}
                                            icon="payments"
                                            searchable
                                            searchPlaceholder={currencyControls.searchPlaceholder}
                                            value={cur}
                                            options={currencyControls.options.map((o) => ({ value: o.value, label: o.label }))}
                                            // Another currency drops a rate typed for the old one.
                                            onChange={(v) => setEditable((prev) => ({ ...prev, currency: v, fx_rate_manual: '' }))}
                                        />
                                        {foreign && (
                                            <Input
                                                label={currencyControls.rateLabel(cur)}
                                                type="number"
                                                min="0"
                                                step="any"
                                                inputMode="decimal"
                                                value={editable.fx_rate_manual}
                                                onChange={(e) => setField('fx_rate_manual', e.target.value)}
                                                title={currencyControls.rateHint}
                                                placeholder="—"
                                            />
                                        )}
                                    </>
                                );
                            })()}
                            <Input
                                label={labels.deductiblePct}
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                inputMode="decimal"
                                value={editable.deductible_pct}
                                onChange={(e) => setField('deductible_pct', e.target.value)}
                                placeholder="100"
                            />
                        </div>
                        {currencyControls?.renderFooter?.(
                            editable.currency || currencyControls.workshopCurrency,
                            (() => { const n = parseAmount(editable.fx_rate_manual); return n !== null && n > 0 ? n : null; })(),
                        )}
                    </>
                ) : (
                    <div className={PAIR_GRID_CLASS}>
                        <div className={`relative min-w-0 ${aiOn('date') ? AI_DATE_WRAP_CLASS : ''}`}>
                            <CustomDatePicker
                                label={labels.date}
                    markRequired
                                selectedDate={editable.date}
                                onChange={(date) => setField('date', date)}
                                monthNames={labels.monthNames}
                                dayNames={labels.dayNames}
                                placeholder={labels.datePlaceholder}
                            />
                            {aiOverlay('date')}
                        </div>
                        <Input
                            label={labels.amount}
                    markRequired
                            type="number"
                            value={editable.amount}
                            onChange={(e) => setField('amount', e.target.value)}
                            placeholder={labels.amountPlaceholder}
                            icon="payments"
                            {...aiInputProps('amount')}
                        />
                    </div>
                )}
            </section>

            <section id="tour-expense-supplier" className={`${SECTION_CLASS} space-y-4`}>
                <SectionHeading icon="receipt_long" className="mb-3">{labels.documentation}</SectionHeading>
                <div className={PAIR_GRID_CLASS}>
                    <Input
                        label={labels.receiptNumber}
                        value={editable.delivery_note}
                        onChange={(e) => setField('delivery_note', e.target.value)}
                        placeholder={labels.receiptNumber}
                        icon="local_shipping"
                        {...aiInputProps('delivery_note')}
                    />
                    <div className="min-w-0">
                        <div className={`relative ${aiOn('supplier') ? AI_SELECT_WRAP_CLASS : ''}`}>
                            <EntitySelector<SupplierPick>
                                label={labels.supplier}
                                placeholder={labels.selectSupplier}
                                emptyLabel={labels.noSuppliers ?? labels.selectSupplier}
                                value={supplierValue}
                                search={runSupplierSearch}
                                onChange={onSupplierChange}
                                {...supplierCreateProps}
                            />
                            {aiOverlay('supplier')}
                        </div>
                        {editable.pending_supplier_name && (
                            <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                                <Icon name="add_business" className="!text-[14px]" aria-hidden="true" />
                                {labels.newSupplierHint}
                            </p>
                        )}
                    </div>
                    <Input
                        label={labels.invoiceNumber}
                        value={editable.invoice_number}
                        onChange={(e) => setField('invoice_number', e.target.value)}
                        placeholder={labels.invoiceNumber}
                        icon="tag"
                        {...aiInputProps('invoice_number')}
                    />
                    <div className={`relative min-w-0 ${aiOn('invoice_date') ? AI_DATE_WRAP_CLASS : ''}`}>
                        <CustomDatePicker
                            label={labels.invoiceDate}
                            selectedDate={editable.invoice_date}
                            onChange={(date) => setField('invoice_date', date)}
                            monthNames={labels.monthNames}
                            dayNames={labels.dayNames}
                            placeholder={labels.datePlaceholder}
                        />
                        {aiOverlay('invoice_date')}
                    </div>
                </div>
            </section>

            <section className={SECTION_CLASS}>
                <SectionHeading icon="attach_file" className="mb-3">{labels.attachments}</SectionHeading>
                <FileAttachmentZone
                    ref={fileZoneRef}
                    existingFiles={editable.files.map((f) => ({
                        id: f.id,
                        name: f.file_name || '',
                        url: f.file_url || '',
                        size: f.file_size,
                        type: f.file_type,
                    }))}
                    onChange={setFileZoneState}
                    accept="document"
                    emptyLabel={labels.addFiles}
                    downloadAriaLabel={labels.download}
                    deleteAriaLabel={labels.delete}
                    undoAriaLabel={labels.undo}
                    uploadBaseUrl={uploadBaseUrl}
                    uploadAuthMode={uploadAuthMode}
                    uploadErrorText={uploadErrorText}
                />
            </section>
        </div>
    );

    return (
        <Shell
            isOpen
            onClose={handleClose}
            dirty={isDirty}
            title={isNew ? labels.newExpense : labels.editExpense(String(expense?.expense_number ?? expense?.id ?? ''))}
            subtitle={isNew ? labels.subtitle : labels.subtitleEdit}
            maxWidth={showPane ? 'max-w-7xl' : showTaxFields ? 'max-w-2xl' : 'max-w-xl'}
            zIndex="z-[51]"
            headerActions={
                <>
                    {onAnalyze && (
                        <FeatureGate enabled={!analyzeLocked} message={analyzeLockedMessage}>
                            <input
                                ref={aiInputRef}
                                type="file"
                                className="hidden"
                                onChange={handleAiImport}
                                disabled={isAnalyzing}
                                accept="image/*,application/pdf"
                            />
                            <Button
                                id="tour-expense-ai"
                                type="button"
                                onClick={() => aiInputRef.current?.click()}
                                disabled={isAnalyzing}
                                variant="primary"
                                icon={isAnalyzing ? 'sync' : 'psychology'}
                                className="!bg-indigo-500 hover:!bg-indigo-600 !shadow-indigo-500/20"
                            >
                                {isAnalyzing ? labels.processing : labels.uploadAI}
                            </Button>
                        </FeatureGate>
                    )}
                    {variant === 'page' ? (
                        <SaveButton
                            onSave={handleSavePage}
                            onSuccess={onSaved}
                            dirty={isDirty}
                            saveLabel={labels.save}
                            savedLabel={labels.saved}
                        />
                    ) : (
                        <Button
                            onClick={handleSaveModal}
                            disabled={isSaving || !isDirty}
                            requires="write"
                            variant="primary"
                            icon={isSaving ? 'sync' : 'save'}
                        >
                            {isSaving ? labels.saving : labels.save}
                        </Button>
                    )}
                    {!isNew && onDelete && (
                        <OverflowMenu
                            items={[
                                {
                                    key: 'delete',
                                    icon: 'delete',
                                    label: labels.deleteExpense,
                                    variant: 'danger',
                                    onClick: onDelete,
                                },
                            ] as OverflowMenuItem[]}
                        />
                    )}
                </>
            }
        >
            {showPane && analysedPreview ? (
                // Not on phones (`useIsMobile`). Beside the form only from `xl`:
                // below it the sidebar leaves the form column too narrow to read
                // the figures the notice asks to review, so the document stacks
                // under the form instead — one layout, styled by width.
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] gap-6 items-start">
                    {form}
                    <aside
                        className={`xl:sticky ${variant === 'page' ? 'xl:top-20' : 'xl:top-0'} ${bgLayer1} p-4 rounded-xl border border-border-subtle dark:border-border-subtle-dark shadow-sm`}
                    >
                        <SectionHeading icon="document_scanner" className="mb-3">{labels.analysedDocument}</SectionHeading>
                        {analysedPreview.kind === 'pdf' ? (
                            <iframe
                                src={analysedPreview.url}
                                title={analysedPreview.name || labels.analysedDocument}
                                className="w-full h-[70vh] rounded-md border border-border-subtle dark:border-border-subtle-dark bg-white"
                            />
                        ) : (
                            <img
                                src={analysedPreview.url}
                                alt={analysedPreview.name || labels.analysedDocument}
                                className="w-full max-h-[70vh] object-contain rounded-md border border-border-subtle dark:border-border-subtle-dark"
                            />
                        )}
                    </aside>
                </div>
            ) : (
                form
            )}
        </Shell>
    );
};

/** The figures in `keys` taken from `from` (the rest are left out). */
function pickFigures(from: ExpenseFigures, keys: ExpenseFigure[]): Partial<ExpenseFigures> {
    const out: Partial<ExpenseFigures> = {};
    keys.forEach((k) => { out[k] = from[k]; });
    return out;
}

export default ExpenseDetailView;
